import { useState, useEffect, useCallback, useMemo, useRef } from "react";

import { useParams } from "react-router-dom";
import Api from "../api/Api";

export default function QuizPage() {
  const { codigo_acesso } = useParams();
    const jaValidouRef = useRef(false); // <-- guarda contra dupla chamada


  // Tema
  const [dark, setDark] = useState(() => {
    if (typeof window !== "undefined") {
      return window.matchMedia?.("(prefers-color-scheme: dark)").matches;
    }
    return false;
  });
  const toggleDark = () => setDark((prev) => !prev);

  // Estados
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [validado, setValidado] = useState(false);
  const [quizData, setQuizData] = useState({
    id_questionario: null,
    id_formacao: null,
    id_user: null,
    duracao: null,
    tempo_restante_segundos: null,
    tempo_restante: null,
  });

  // Temporizador
  const [remainingSeconds, setRemainingSeconds] = useState(null);
  const [timerInterval, setTimerInterval] = useState(null);

  // Perguntas e respostas
  const [perguntas, setPerguntas] = useState([]);
  const [respostasPorPergunta, setRespostasPorPergunta] = useState({});
  const [selectedAnswers, setSelectedAnswers] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [quizFinished, setQuizFinished] = useState(false);
  const [finishMessage, setFinishMessage] = useState("");
  const [loadingPerguntas, setLoadingPerguntas] = useState(false); // inicia como false, pois será ativado após validação

  // Cores
  const t = useMemo(() => {
    if (dark) {
      return {
        bg: "#080c14",
        card: "#111827",
        border: "rgba(0,174,255,0.12)",
        primary: "#eef5ff",
        secondary: "#b0c7e0",
        muted: "#7b93b0",
        accent: "#00aaff",
        danger: "#ef4444",
        success: "#22c55e",
        overlay: "rgba(8,12,20,0.8)",
      };
    } else {
      return {
        bg: "#f4f7fc",
        card: "#ffffff",
        border: "#e2e8f0",
        primary: "#0f1a2e",
        secondary: "#475569",
        muted: "#8b9eb0",
        accent: "#0057ff",
        danger: "#ef4444",
        success: "#16a34a",
        overlay: "rgba(15,23,42,0.4)",
      };
    }
  }, [dark]);

  const formatTime = (segundos) => {
    if (segundos == null || isNaN(segundos)) return "--:--:--";
    const h = Math.floor(segundos / 3600);
    const m = Math.floor((segundos % 3600) / 60);
    const s = Math.floor(segundos % 60);
    return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  };

  const parseTimeToSeconds = (str) => {
    if (!str) return 0;
    const [h, m, s] = str.split(":").map(Number);
    return (h || 0) * 3600 + (m || 0) * 60 + (s || 0);
  };

  // Função para carregar perguntas e respostas
  const loadQuestions = async (data) => {
    if (!data.id_user || !data.id_formacao || !data.id_questionario) {
      setError("Dados incompletos para carregar perguntas.");
      setValidado(false);
      return;
    }
    setLoadingPerguntas(true);
    try {
      const res = await Api.getPerguntaQuiz({
        id_user: data.id_user,
        id_formacao: data.id_formacao,
        id_questionario: data.id_questionario,
      });

      if (!res?.success) {
        setError(res?.message || "Erro ao carregar perguntas.");
        setValidado(false);
        return;
      }

      const perguntasArray = res.perguntas || [];
      if (perguntasArray.length === 0) {
        setQuizFinished(true);
        setFinishMessage("Não há perguntas disponíveis.");
        return;
      }

      setPerguntas(perguntasArray);
      const initSel = {};
      perguntasArray.forEach(p => { initSel[p.id] = null; });
      setSelectedAnswers(initSel);

      const respostasMap = {};
      const promessas = perguntasArray.map(async (p) => {
        const respRes = await Api.getResposta({ id_pergunta: p.id });
        const respostas = Array.isArray(respRes?.data) ? respRes.data : Array.isArray(respRes) ? respRes : [];
        respostasMap[p.id] = respostas;
      });
      await Promise.all(promessas);
      setRespostasPorPergunta(respostasMap);
    } catch (e) {
      setError("Erro ao carregar perguntas.");
    } finally {
      setLoadingPerguntas(false);
    }
  };

  // Validação do código e carregamento das perguntas
  const validar = useCallback(async () => {
    if (jaValidouRef.current) return; // já está validando/validou, ignora
    jaValidouRef.current = true;

    setLoading(true);
    setError(null);
    try {
      const res = await Api.validarQiz({ codigo_acesso });
            console.log("Resposta validarQiz:", res); // temporário, para depurar

      if (!res?.success) {
        setError(res?.message || "Código inválido ou questionário já concluído.");
        setValidado(false);
        return;
      }

      const data = {
        id_questionario: res.id_questionario,
        id_formacao: res.id_formacao,
        id_user: res.id_user,
        duracao: res.duracao || null,
        tempo_restante_segundos: res.tempo_restante_segundos || null,
        tempo_restante: res.tempo_restante || null,
      };

      setQuizData(data);
      setValidado(true);

      let segundos;
      if (data.tempo_restante_segundos != null) {
        segundos = Math.floor(data.tempo_restante_segundos);
      } else if (data.duracao) {
        segundos = parseTimeToSeconds(data.duracao);
      } else {
        segundos = 3600;
      }
      setRemainingSeconds(segundos);

      // Carrega as perguntas imediatamente após a validação
      await loadQuestions(data);
    } catch (err) {
      setError("Erro ao validar o questionário.");
      setValidado(false);
    } finally {
      setLoading(false);
    }
  }, [codigo_acesso]);

  useEffect(() => { validar(); }, [validar]);

  // Contagem regressiva (melhorada para não recriar o intervalo a cada segundo)
  useEffect(() => {
    if (!validado || remainingSeconds == null) return;
    const id = setInterval(() => {
      setRemainingSeconds((prev) => {
        if (prev == null || prev <= 0) {
          clearInterval(id);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => clearInterval(id);
  }, [validado]); // dependência apenas de validado

  // Tempo esgotado
  useEffect(() => {
    if (validado && remainingSeconds === 0 && !quizFinished) {
      handleTimeUp();
    }
  }, [remainingSeconds, validado, quizFinished]);

  const handleTimeUp = () => {
    setQuizFinished(true);
    setFinishMessage("O tempo limite foi atingido. As suas respostas foram guardadas.");
  };

  // Enviar todas as respostas
 const handleSubmitAll = async () => {
  const faltam = Object.values(selectedAnswers).some(val => val === null);
  if (faltam) {
    alert("Por favor, responda todas as perguntas antes de enviar.");
    return;
  }
  
  setSubmitting(true);
  try {
    // Monta os arrays paralelos
    const id_pergunta = perguntas.map(p => p.id);
    const id_resposta = perguntas.map(p => selectedAnswers[p.id]);

    await Api.addRespostaQuiz({
      id_questionario: quizData.id_questionario,
      id_pergunta: id_pergunta,
      id_resposta: id_resposta
    });

    setQuizFinished(true);
    setFinishMessage("Questionário concluído! Irá receber o resultado por e-mail.");
  } catch (e) {
    alert("Erro ao enviar respostas. Tente novamente.");
  } finally {
    setSubmitting(false);
  }
};

  // --------------- Layout ---------------
  return (
    <div style={{
      minHeight: "100vh",
      background: t.bg,
      fontFamily: "'Inter', system-ui, sans-serif",
      padding: 20,
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      color: t.primary,
    }}>
      {/* Botão de tema */}
      <div style={{ position: "fixed", top: 16, right: 16, zIndex: 999 }}>
        <button
          onClick={toggleDark}
          style={{
            background: t.card,
            border: `1px solid ${t.border}`,
            borderRadius: 12,
            color: t.primary,
            padding: "8px 12px",
            cursor: "pointer",
            fontSize: 13,
            fontWeight: 600,
          }}
        >
          {dark ? "☀️ Claro" : "🌙 Escuro"}
        </button>
      </div>

      {/* Overlay de envio */}
      {submitting && (
        <div style={{
          position: "fixed",
          inset: 0,
          background: t.overlay,
          backdropFilter: "blur(6px)",
          zIndex: 1000,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 16,
        }}>
          <div style={{
            width: 48,
            height: 48,
            border: "4px solid rgba(255,255,255,0.2)",
            borderTopColor: t.accent,
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }} />
          <div style={{ color: t.primary, fontWeight: 600, fontSize: "1rem" }}>
            Enviando respostas...
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>

      <div style={{ width: "100%", maxWidth: 800, marginTop: 40 }}>
        {loading ? (
          <div style={{ textAlign: "center", color: t.muted }}>A validar questionário…</div>
        ) : error && !validado ? (
          <div style={{
            background: dark ? "rgba(239,68,68,0.12)" : "#fef2f2",
            color: t.danger,
            padding: 20,
            borderRadius: 16,
            border: `1px solid ${t.danger}33`,
            textAlign: "center",
            fontWeight: 500,
          }}>
            {error}
          </div>
        ) : validado ? (
          <>
            {/* Cabeçalho */}
            <div style={{
              background: t.card,
              border: `1px solid ${t.border}`,
              borderRadius: 18,
              padding: "20px 24px",
              marginBottom: 24,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "wrap",
              gap: 12,
            }}>
              <div style={{ fontWeight: 700, fontSize: "1.2rem" }}>Questionário</div>
              <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
                <div style={{ fontSize: "0.85rem", color: t.secondary }}>
                  {perguntas.length > 0 ? (
                    <span>
                      Respondidas: <strong style={{ color: t.accent }}>
                        {Object.values(selectedAnswers).filter(v => v !== null).length}
                      </strong> de {perguntas.length}
                    </span>
                  ) : (
                    <span>Aguarde…</span>
                  )}
                </div>
                <div style={{
                  background: remainingSeconds <= 60 ? t.danger + "22" : "transparent",
                  color: remainingSeconds <= 60 ? t.danger : t.primary,
                  padding: "6px 14px",
                  borderRadius: 12,
                  fontWeight: 700,
                  fontSize: "0.9rem",
                  border: remainingSeconds <= 60 ? `1px solid ${t.danger}44` : "none",
                  letterSpacing: 1,
                }}>
                  ⏳ {formatTime(remainingSeconds)}
                </div>
              </div>
            </div>

            {quizFinished ? (
              <div style={{
                background: t.card,
                border: `1px solid ${t.border}`,
                borderRadius: 18,
                padding: 40,
                textAlign: "center",
              }}>
                <div style={{ fontSize: "1.3rem", fontWeight: 700, marginBottom: 12 }}>
                  Questionário concluído!
                </div>
                <p style={{ color: t.secondary, marginBottom: 24 }}>{finishMessage}</p>
                <div style={{ color: t.muted, fontSize: "0.85rem" }}>
                  Pode fechar esta página.
                </div>
              </div>
            ) : loadingPerguntas ? (
              <div style={{ textAlign: "center", color: t.muted, padding: 40 }}>A carregar perguntas…</div>
            ) : perguntas.length === 0 ? (
              <div style={{ textAlign: "center", color: t.muted, padding: 40 }}>Nenhuma pergunta disponível.</div>
            ) : (
              <>
                {perguntas.map((pergunta) => (
                  <div key={pergunta.id} style={{
                    background: t.card,
                    border: `1px solid ${t.border}`,
                    borderRadius: 18,
                    padding: "24px 28px",
                    marginBottom: 20,
                  }}>
                    <div style={{ fontWeight: 600, fontSize: "1.05rem", marginBottom: 16 }}>
                      {pergunta.pergunta}
                    </div>

                    {respostasPorPergunta[pergunta.id]?.length > 0 ? (
                      <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                        {respostasPorPergunta[pergunta.id].map((resp) => (
                          <label
                            key={resp.id}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: 12,
                              padding: "12px 16px",
                              borderRadius: 14,
                              border: `1px solid ${selectedAnswers[pergunta.id] === resp.id ? t.accent : t.border}`,
                              background: selectedAnswers[pergunta.id] === resp.id
                                ? (dark ? "rgba(0,180,255,0.12)" : "#e6f0ff")
                                : "transparent",
                              cursor: "pointer",
                              fontWeight: 500,
                              color: t.primary,
                              transition: "0.2s",
                            }}
                            onClick={() => setSelectedAnswers(prev => ({ ...prev, [pergunta.id]: resp.id }))}
                          >
                            <input
                              type="radio"
                              name={`pergunta-${pergunta.id}`}
                              checked={selectedAnswers[pergunta.id] === resp.id}
                              onChange={() => setSelectedAnswers(prev => ({ ...prev, [pergunta.id]: resp.id }))}
                              style={{ accentColor: t.accent, width: 18, height: 18 }}
                            />
                            <span>{resp.resposta}</span>
                          </label>
                        ))}
                      </div>
                    ) : (
                      <div style={{ color: t.muted }}>A carregar alternativas…</div>
                    )}
                  </div>
                ))}

                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 8 }}>
                  <button
                    onClick={handleSubmitAll}
                    disabled={submitting || Object.values(selectedAnswers).some(v => v === null)}
                    style={{
                      height: 44,
                      padding: "0 28px",
                      borderRadius: 12,
                      border: "none",
                      background: submitting || Object.values(selectedAnswers).some(v => v === null)
                        ? t.muted
                        : "linear-gradient(135deg, #00aaff, #0044ee)",
                      color: "#fff",
                      fontWeight: 600,
                      fontSize: 14,
                      cursor: submitting || Object.values(selectedAnswers).some(v => v === null) ? "not-allowed" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      gap: 8,
                    }}
                  >
                    {submitting ? "A enviar..." : "Enviar todas as respostas"}
                  </button>
                </div>
              </>
            )}
          </>
        ) : null}
      </div>
    </div>
  );
}